import { useState, useEffect, useReducer, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import toastError from '../../toasts/toastError';

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';

import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import Title from '../../components/Title';
import TableRowSkeleton from '../../components/TableRowSkeleton';
import CompanyModal from '../../components/CompaniesModal';
import ConfirmationModal from '../../components/ConfirmationModal';

import api from '../../services/api';
import { i18n } from '../../translate/i18n';
import { AuthContext } from '../../context/Auth/AuthContext';
import { useDate } from '../../hooks/useDate';
import moment from 'moment';
import { getStyled } from './styles';

const reducer = (state, action) => {
  if (action.type === 'LOAD_COMPANIES') {
    const companies = action.payload;
    const newCompanies = [];

    companies.forEach((company) => {
      const companyIndex = state.findIndex((u) => u.id === company.id);
      if (companyIndex !== -1) {
        state[companyIndex] = company;
      } else {
        newCompanies.push(company);
      }
    });

    return [...state, ...newCompanies];
  }

  if (action.type === 'UPDATE_COMPANIES') {
    const company = action.payload;
    const companyIndex = state.findIndex((u) => u.id === company.id);

    if (companyIndex !== -1) {
      state[companyIndex] = company;
      return [...state];
    } else {
      return [company, ...state];
    }
  }

  if (action.type === 'DELETE_COMPANIES') {
    const companyId = action.payload;

    const companyIndex = state.findIndex((u) => u.id === companyId);
    if (companyIndex !== -1) {
      state.splice(companyIndex, 1);
    }
    return [...state];
  }

  if (action.type === 'RESET') {
    return [];
  }
};

const Companies = () => {
  const theme = useTheme();
  const classes = getStyled(theme);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [deletingCompany, setDeletingCompany] = useState(null);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState('');
  const [companies, dispatch] = useReducer(reducer, []);
  const { dateToClient, datetimeToClient } = useDate();

  const { user } = useContext(AuthContext);

  useEffect(() => {
    async function fetchData() {
      if (!user.super) {
        toastError(
          'Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.',
        );
        setTimeout(() => {
          navigate(`/`);
        }, 1000);
      }
    }
    fetchData();
  }, [user, navigate]);

  useEffect(() => {
    dispatch({ type: 'RESET' });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    const fetchCompanies = async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/companiesPlan/', {
          params: { searchParam, pageNumber },
        });
        dispatch({ type: 'LOAD_COMPANIES', payload: data.companies });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [searchParam, pageNumber]);

  const handleCloseCompanyModal = () => {
    setSelectedCompany(null);
    setCompanyModalOpen(false);
  };

  const handleDeleteCompany = async (companyId) => {
    try {
      await api.delete(`/companies/${companyId}`);
      toastSuccess(i18n.t('compaies.toasts.deleted'));
    } catch (err) {
      toastError(err);
    }
    setDeletingCompany(null);
    setSearchParam('');
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const renderStatus = (row) => {
    return row.status === false ? 'Não' : 'Sim';
  };

  const renderPlanValue = (row) => {
    return row.planId !== null
      ? row.plan.amount
        ? row.plan.amount.toLocaleString('pt-br', { minimumFractionDigits: 2 })
        : '00.00'
      : '-';
  };

  const rowStyle = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, 'days');
      if (diff >= 1 && diff <= 5) {
        return { backgroundColor: '#fffead' };
      }
      if (diff <= 0) {
        return { backgroundColor: '#fa8c8c' };
      }
    }
    return {};
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingCompany &&
          `${i18n.t('compaies.confirmationModal.deleteTitle')} ${
            deletingCompany.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCompany(deletingCompany.id)}
      >
        {i18n.t('compaies.confirmationModal.deleteMessage')}
      </ConfirmationModal>
      <CompanyModal
        open={companyModalOpen}
        onClose={handleCloseCompanyModal}
        aria-labelledby='form-dialog-title'
        companyId={selectedCompany && selectedCompany.id}
      />
      <MainHeader>
        <Title>
          {i18n.t('compaies.title')} ({companies.length})
        </Title>
      </MainHeader>
      <Paper sx={classes.mainPaper} variant='outlined' onScroll={handleScroll}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell align='center'>
                {i18n.t('compaies.table.ID')}
              </TableCell>
              <TableCell align='center'>
                {i18n.t('compaies.table.status')}
              </TableCell>
              <TableCell align='center'>
                {i18n.t('compaies.table.name')}
              </TableCell>
              <TableCell align='center'>
                {i18n.t('compaies.table.email')}
              </TableCell>
              <TableCell align='center'>
                {i18n.t('compaies.table.namePlan')}
              </TableCell>
              <TableCell align='center'>
                {i18n.t('compaies.table.value')}
              </TableCell>
              <TableCell align='center'>
                {i18n.t('compaies.table.createdAt')}
              </TableCell>
              <TableCell align='center'>
                {i18n.t('compaies.table.dueDate')}
              </TableCell>
              <TableCell align='center'>
                {i18n.t('compaies.table.lastLogin')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {companies.map((company) => (
                <TableRow style={rowStyle(company)} key={company.id}>
                  <TableCell align='center'>{company.id}</TableCell>
                  <TableCell align='center'>
                    {renderStatus(company.status)}
                  </TableCell>
                  <TableCell align='center'>{company.name}</TableCell>
                  <TableCell align='center'>{company.email}</TableCell>
                  <TableCell align='center'>{company?.plan?.name}</TableCell>
                  <TableCell align='center'>
                    R$ {renderPlanValue(company)}
                  </TableCell>
                  <TableCell align='center'>
                    {dateToClient(company.createdAt)}
                  </TableCell>
                  <TableCell align='center'>
                    {dateToClient(company.dueDate)}
                    <br />
                    <span>{company.recurrence}</span>
                  </TableCell>
                  <TableCell align='center'>
                    {datetimeToClient(company.lastLogin)}
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={4} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Companies;
